import React, { useState, useMemo, useEffect } from 'react';
import MaterialTable from 'material-table';
import Pagination from 'react-js-pagination';
import { Grid, NativeSelect, Paper } from '@material-ui/core';
import { toast } from 'react-toastify';
import { useHistory } from 'react-router-dom';

import Header from '../../layout/Header';
import Subheader from '../../layout/Subheader';
import Loading from '../../components/Loading';
import api from '../../services/api';
import { findPermission, TOKEN_PAGINATION } from '../../services/auth';
import {
  consultar,
  STATUS_OPTIONS,
  SITUACAO_LABELS,
} from '../../services/erpsApisControlesBaixasTitulosAntecipadosService';
import useStyles from './style';

// ModuloId derivado via dbo.fnCalculaModuloId('ErpsApisControlesBaixasTitulosAntecipados').
const MODULO_ID = 90153;

function formatDateTime(d) {
  if (!d) return '-';
  try {
    return new Date(d).toLocaleString('pt-BR');
  } catch (e) {
    return '-';
  }
}

function statusInfo(status) {
  return STATUS_OPTIONS.find((s) => s.value === status) || { label: status, color: '#777' };
}

export function ErpsApisControlesBaixasTitulosAntecipados() {
  const classes = useStyles();
  const history = useHistory();

  const [isLoading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itensPerPage, setItensPerPage] = useState(50);
  const [pagination, setPagination] = useState({});
  const [vinculosPorId, setVinculosPorId] = useState({});

  useEffect(() => {
    if (!findPermission(MODULO_ID, 'Consultar')) {
      history.replace('/app/home');
    }
  }, [history]);

  // Enriquece a listagem com nomes amigaveis: o endpoint Consultar da tabela de
  // baixas nao faz join, entao resolvemos Ancora/Vinculo a partir das telas ja
  // existentes (Ancoras + vinculos Ancora x ERP).
  const fetchVinculos = async () => {
    try {
      const [resAncoras, resVinculos] = await Promise.all([
        api.get('Ancoras/Consultar?status=Ativo'),
        api.get('AncorasImportacoesTitulosApisErps/Consultar?paginaInicial=0&itensPorPagina=0'),
      ]);
      const ancorasPorId = {};
      (resAncoras?.data?.resposta || []).forEach((a) => {
        ancorasPorId[a.ancoraId] = a.nomeFantasia;
      });
      const mapa = {};
      (resVinculos?.data?.resposta || []).forEach((v) => {
        mapa[v.ancoraImportacaoTituloApiErpId] = {
          apelido: v.apelido || `Vínculo ${v.ancoraImportacaoTituloApiErpId}`,
          ancoraNome: ancorasPorId[v.ancoraId] || '-',
        };
      });
      setVinculosPorId(mapa);
    } catch (e) {
      setVinculosPorId({});
    }
  };

  const memoizedTable = useMemo(
    () => (
      <MaterialTable
        className={classes.table}
        options={{
          showTitle: false,
          search: false,
          toolbar: false,
          headerStyle: {
            backgroundColor: '#F5F5F5',
            textTransform: 'none',
            fontSize: '15px',
          },
          filtering: false,
          paging: false,
          showEmptyDataSourceMessage: false,
        }}
        columns={[
          {
            title: 'Título',
            field: 'antecipacaoTituloId',
            cellStyle: { fontSize: '13px', padding: '8px 16px' },
          },
          {
            title: 'Âncora / Vínculo',
            field: 'ancoraImportacaoTituloApiErpId',
            cellStyle: { fontSize: '13px', padding: '8px 16px' },
            render: (r) => {
              const v = vinculosPorId[r.ancoraImportacaoTituloApiErpId];
              if (!v) return `Vínculo ${r.ancoraImportacaoTituloApiErpId}`;
              return `${v.ancoraNome} — ${v.apelido}`;
            },
          },
          {
            title: 'Nº Documento',
            field: 'parametroStr01',
            cellStyle: { fontSize: '13px', padding: '8px 16px' },
          },
          {
            title: 'Descritivo',
            field: 'parametroStr03',
            cellStyle: { fontSize: '13px', padding: '8px 16px' },
          },
          {
            title: 'Status',
            field: 'status',
            cellStyle: { fontSize: '13px', padding: '8px 10px', width: '150px' },
            render: (r) => (
              <span
                className={classes.statusChip}
                style={{ background: statusInfo(r.status).color }}
              >
                {statusInfo(r.status).label}
              </span>
            ),
          },
          {
            title: 'Situação',
            field: 'situacao',
            cellStyle: { fontSize: '13px', padding: '8px 16px' },
            render: (r) => SITUACAO_LABELS[r.situacao] || r.situacao || '-',
          },
          {
            title: 'Data Baixa',
            field: 'dataBaixa',
            cellStyle: { fontSize: '13px', padding: '8px 16px' },
            render: (r) => formatDateTime(r.dataBaixa),
          },
          {
            title: 'Registrado em',
            field: 'dataRegistro',
            cellStyle: { fontSize: '13px', padding: '8px 16px' },
            render: (r) => formatDateTime(r.dataRegistro),
          },
        ]}
        data={items}
      />
    ),
    [items, classes, vinculosPorId],
  );

  const fetchItens = async (pageNumber = currentPage) => {
    setLoading(true);
    try {
      const res = await consultar({
        paginaInicial: pageNumber,
        itensPorPagina: itensPerPage,
        status,
      });

      if (res.data.status !== 'Sucesso') {
        if (res.data.resposta?.length > 0) {
          res.data.resposta.forEach((el) => toast.error(el.message));
        }
        setItems([]);
        setPagination({});
        return;
      }

      setPagination(res.data.paginacao || {});
      if (res.data.paginacao?.qtdTotalRegistros > 0) {
        setItems(res.data.resposta || []);
      } else {
        setItems([]);
      }
    } catch (err) {
      toast.error('Detectamos um problema interno! tente novamente em instantes.');
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    fetchItens(pageNumber);
  };

  useEffect(() => {
    const userPage = JSON.parse(sessionStorage.getItem(TOKEN_PAGINATION))
      ?.paginacao?.itensPorPagina;
    setItensPerPage(userPage === 0 || !userPage ? 50 : userPage);
    fetchVinculos();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
    const t = setTimeout(() => fetchItens(1), 800);
    return () => clearTimeout(t);
  }, [itensPerPage]);

  return (
    <div className={classes.root}>
      <Header />
      <Subheader title="Controle de Baixa de Títulos (ERP)" back="/app/home" />
      <div className={classes.content}>
        <div className={classes.rootForm}>
          <header>
            <Grid container className={classes.searchContainer} spacing={2}>
              <Grid item xs={3} className={classes.item}>
                <label className={classes.label}>Status</label>
                <Paper className={classes.medium} elevation={0}>
                  <NativeSelect
                    className={classes.input}
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                  >
                    <option value="">Todos</option>
                    {STATUS_OPTIONS.map((s) => (
                      <option key={s.value} value={s.value}>
                        {s.label}
                      </option>
                    ))}
                  </NativeSelect>
                </Paper>
              </Grid>
            </Grid>
          </header>
          <div className={classes.actions}>
            <button className={classes.buttonFilter} onClick={() => fetchItens(1)}>
              Pesquisar
            </button>
          </div>
          {isLoading ? (
            <Loading />
          ) : (
            <>
              {memoizedTable}
              <div className="footer-table">
                <Pagination
                  activePage={currentPage}
                  itemsCountPerPage={itensPerPage}
                  totalItemsCount={pagination.qtdTotalRegistros || 0}
                  pageRangeDisplayed={5}
                  onChange={handlePageChange}
                />
                <div className="select-page">
                  <label>Qtd. por página</label>
                  <NativeSelect
                    onChange={(e) => setItensPerPage(Number(e.currentTarget.value))}
                    value={itensPerPage}
                  >
                    <option value="25">25</option>
                    <option value="50">50</option>
                    <option value="100">100</option>
                    <option value="200">200</option>
                  </NativeSelect>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default ErpsApisControlesBaixasTitulosAntecipados;
